import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Option, Turn } from "../api/types";
import type { ConversationState, LogEntry } from "../conversation/types";
import { OptionList } from "./OptionList";
import { CrisisCard, ChoiceCard, MatchCard, SummaryCard } from "./Cards";
import { useI18n } from "../i18n";

type ChatFeedProps = {
  state: ConversationState;
  onSelect: (message: string, displayText: string) => void;
  onProceed: (service: string, proceed: Option) => void;
  onRetry: () => void;
  /** Shown on the completion screen once a referral has been submitted. */
  referralReference?: string | null;
  /** Open the Joy instructions modal — the coach route offered on the
   *  data-sharing decline close. */
  onMessageCoach?: () => void;
};

/** A bot message row (no avatar, full-width left, gemini-style). */
function BotRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={`message-row bot${className ? ` ${className}` : ""}`}>
      <div className="bot-content-block">{children}</div>
    </div>
  );
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="message-row user">
      <div className="user-message-bubble">{text}</div>
    </div>
  );
}

function TypingIndicator() {
  const { t } = useI18n();
  return (
    <div className="message-row bot">
      <div className="bot-content-block">
        <div className="typing-bubble-dots" role="status" aria-label={t("chat.typing")}>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const { t } = useI18n();
  return (
    <div className="bot-utility-row">
      <button
        type="button"
        className="bot-util-btn"
        aria-label={t("chat.copyMessage")}
        title={t("chat.copy")}
        onClick={() => void navigator.clipboard?.writeText(text)}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </button>
    </div>
  );
}

/** Staged-reveal pause before a question shows (after its empathy ack). Shared
 *  with App so the input chips reveal in lock-step with the question. */
export const revealDelay = (message?: string | null) =>
  Math.min(1600, 600 + (message?.length ?? 0) * 12);

const isMatchTurn = (t: Turn) =>
  !!t.node_id && (t.node_id.startsWith("CONSENT_PROCEED") || t.node_id.startsWith("CONSENT_ALT")) && !!t.service_recommended;
const isChoiceTurn = (t: Turn) =>
  !!t.options && t.options.length > 0 && t.options.some((o) => o.description);
// The "Referred to <service> — details sent securely" summary card must ONLY
// render when a referral was actually transmitted. A terminal turn can still
// carry a stale service_recommended after the resident DECLINED (e.g. the warm
// decline close), so gating on service_recommended alone falsely tells a
// declined resident their details were sent. Only the post-transmission close
// nodes (CLOSE_OK_*) represent a completed referral.
const REFERRAL_COMPLETE_NODES = new Set(["CLOSE_OK_WW", "CLOSE_OK_C2W"]);
const isSummaryTurn = (t: Turn) =>
  t.terminal && !!t.service_recommended && !!t.node_id && REFERRAL_COMPLETE_NODES.has(t.node_id);

// The data-sharing decline close makes the coach route actionable: the phrase
// "Send a Message to a WorkWell Coach" already appears in the message copy, so
// it becomes an inline underlined link there (not a separate button — Naveen
// 17 Jul). It's the one onward action left to a resident who chose not to share.
const DECLINE_SHARE_CLOSE_NODES = new Set(["CLOSE_DECLINE_SHARE_WW", "CLOSE_DECLINE_SHARE_C2W"]);
const offersCoachLink = (t: Turn) => !!t.node_id && DECLINE_SHARE_CLOSE_NODES.has(t.node_id);

const COACH_PHRASE = "Send a Message to a WorkWell Coach";

/** The close message with the coach phrase rendered as an inline link that
 *  opens the Joy instructions modal. Falls back to plain text if the phrase
 *  ever disappears from the copy. */
function CoachLinkedMessage({
  text,
  onMessageCoach,
}: {
  text: string;
  onMessageCoach: () => void;
}) {
  const idx = text.indexOf(COACH_PHRASE);
  if (idx === -1) return <div className="bot-message-text">{text}</div>;
  return (
    <div className="bot-message-text">
      {text.slice(0, idx)}
      <button type="button" className="chat-coach-link" onClick={onMessageCoach}>
        {COACH_PHRASE}
      </button>
      {text.slice(idx + COACH_PHRASE.length)}
    </div>
  );
}

function BotTurn({
  turn,
  active,
  busy,
  onSelect,
  onProceed,
  referralReference,
  onMessageCoach,
}: {
  turn: Turn;
  active: boolean;
  busy: boolean;
  onSelect: ChatFeedProps["onSelect"];
  onProceed: ChatFeedProps["onProceed"];
  referralReference?: string | null;
  onMessageCoach?: ChatFeedProps["onMessageCoach"];
}) {
  if (turn.crisis) return <BotRow><CrisisCard message={turn.message} /></BotRow>;
  if (offersCoachLink(turn) && onMessageCoach) {
    return (
      <BotRow>
        <CoachLinkedMessage text={turn.message} onMessageCoach={onMessageCoach} />
      </BotRow>
    );
  }
  if (isMatchTurn(turn)) {
    return (
      <BotRow>
        <MatchCard turn={turn} active={active} busy={busy} onSelect={onSelect} onProceed={onProceed} />
      </BotRow>
    );
  }
  if (isChoiceTurn(turn)) {
    return (
      <BotRow>
        <ChoiceCard turn={turn} active={active} busy={busy} onSelect={onSelect} />
      </BotRow>
    );
  }
  if (isSummaryTurn(turn))
    return <BotRow><SummaryCard turn={turn} reference={referralReference} /></BotRow>;

  return <NormalBotTurn turn={turn} active={active} busy={busy} onSelect={onSelect} />;
}

/**
 * A normal question turn. When the engine returns an empathetic acknowledgment
 * *and* a scripted question, show them as two messages: the acknowledgment
 * first, then a typing indicator, then the question — mimicking the assistant
 * composing the next message. Only the freshly-arrived (active) turn animates;
 * historical turns render in full immediately.
 */
function NormalBotTurn({
  turn,
  active,
  busy,
  onSelect,
}: {
  turn: Turn;
  active: boolean;
  busy: boolean;
  onSelect: ChatFeedProps["onSelect"];
}) {
  const stage = active && !!turn.acknowledgment && !!turn.message;
  const [revealed, setRevealed] = useState(!stage);

  // Pause, then reveal the question (natural-feeling, scaled to its length).
  useEffect(() => {
    if (revealed) return;
    const t = setTimeout(() => setRevealed(true), revealDelay(turn.message));
    return () => clearTimeout(t);
  }, [revealed, turn.message]);

  // The staged question used to scroll the feed to the bottom when it
  // revealed — removed (Priya, PROJ-119): the feed anchors at the START of
  // the response, and the question appears in place below it.

  return (
    <>
      {turn.preamble?.map((p, i) => (
        <BotRow key={`pre-${i}`}>
          <div className="bot-message-text">{p}</div>
        </BotRow>
      ))}
      {/* Empathetic acknowledgment as its own message, kept close to the
          question that follows (it's one AI response split in two). */}
      {turn.acknowledgment && (
        <BotRow className="ack-row">
          <div className="bot-message-text">
            <span className="bot-ack-inline">{turn.acknowledgment}</span>
          </div>
        </BotRow>
      )}
      {!revealed ? (
        <TypingIndicator />
      ) : (
        (turn.message || (turn.options?.length ?? 0) > 0) && (
          <BotRow>
            {turn.message && <div className="bot-message-text">{turn.message}</div>}
            {turn.subtext && <div className="bot-message-subtext">{turn.subtext}</div>}
            <OptionList turn={turn} active={active} busy={busy} onSelect={onSelect} />
            {turn.message && <CopyButton text={turn.message} />}
          </BotRow>
        )
      )}
    </>
  );
}

function lastBotId(log: LogEntry[]): string | null {
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].role === "bot") return log[i].id;
  }
  return null;
}

export function ChatFeed({
  state,
  onSelect,
  onProceed,
  onRetry,
  referralReference,
  onMessageCoach,
}: ChatFeedProps) {
  const { t } = useI18n();
  const endRef = useRef<HTMLDivElement>(null);
  const lastBot = lastBotId(state.log);
  const busy = state.status === "sending";

  useEffect(() => {
    const feed = document.querySelector<HTMLElement>(".chat-feed");
    const last = state.log[state.log.length - 1];
    // A NEW BOT RESPONSE anchors at its START, so the resident reads from the
    // beginning and scrolls down naturally (Priya, PROJ-119) — jumping to the
    // bottom of a long answer made them scroll UP to find where it began.
    if (feed && last && last.role === "bot" && state.status === "active") {
      const rows = Array.from(feed.querySelectorAll<HTMLElement>(".chat-log > *"));
      let anchor: HTMLElement | null = null;
      for (let i = rows.length - 1; i >= 0; i--) {
        if (rows[i].classList.contains("user")) {
          anchor = rows[i + 1] ?? null;
          break;
        }
      }
      if (anchor && typeof feed.scrollTo === "function") {
        const top =
          anchor.getBoundingClientRect().top -
          feed.getBoundingClientRect().top +
          feed.scrollTop -
          10;
        feed.scrollTo({ top, behavior: "smooth" });
      }
      // No user rows yet = the opening turn — the feed is already at the top.
      return;
    }
    // Everything else (own message sent, typing indicator, errors): follow to
    // the end as before, so the resident sees their bubble land.
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [state.log, state.status]);

  return (
    <>
      {state.log.map((entry) =>
        entry.role === "user" ? (
          <UserBubble key={entry.id} text={entry.text} />
        ) : (
          <BotTurn
            key={entry.id}
            turn={entry.turn}
            active={entry.id === lastBot && state.status === "active"}
            busy={busy}
            onSelect={onSelect}
            onProceed={onProceed}
            referralReference={referralReference}
            onMessageCoach={onMessageCoach}
          />
        ),
      )}

      {(state.status === "sending" || state.status === "starting") && <TypingIndicator />}

      {state.status === "error" && (
        <div className="error-notice" role="alert">
          <span>{state.error}</span>
          <button type="button" className="error-notice__retry" onClick={onRetry}>
            {t("chat.tryAgain")}
          </button>
        </div>
      )}

      <div ref={endRef} />
    </>
  );
}
